import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useChatContext } from '../../../context/ChatContext';
import { processDocument, isFileTypeSupported, getFileTypeInfo, getSupportedExtensions, formatFileSize } from '../../../utils/documentProcessors';

const { FiMessageSquare, FiTrash2, FiUpload, FiFile, FiX, FiCheck, FiAlertTriangle, FiInfo, FiBookOpen, FiSearch, FiFileText, FiRefreshCw, FiSettings, FiSliders } = FiIcons;

const ChatSettings = () => {
  const {
    knowledgeBase,
    addKnowledgeBaseDocument,
    removeKnowledgeBaseDocument,
    clearMessages,
    chatLoading,
    config,
    messageHistoryLimit,
    updateMessageHistoryLimit,
  } = useChatContext();

  const [saveStatus, setSaveStatus] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deletingDocuments, setDeletingDocuments] = useState(new Set());
  const [confirmDeleteDocument, setConfirmDeleteDocument] = useState(null);

  const handleRemoveDocument = async (documentId, documentName) => {
    if (confirmDeleteDocument !== documentId) {
      setConfirmDeleteDocument(documentId);
      setTimeout(() => {
        setConfirmDeleteDocument(null);
      }, 5000);
      return;
    }

    setDeletingDocuments(prev => new Set(prev).add(documentId));

    try {
      console.log('Removing document:', { documentId, documentName });
      await removeKnowledgeBaseDocument(documentId);
      console.log('Document removed successfully');

      setConfirmDeleteDocument(null);
      setSaveStatus(`Document "${documentName}" removed successfully!`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error removing document:', error);
      setSaveStatus(`Failed to remove "${documentName}". Please try again.`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setDeletingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Starting file upload:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    setUploadStatus('');
    setIsProcessing(true);

    try {
      if (!isFileTypeSupported(file)) {
        throw new Error('Unsupported file type. Please upload PDF, Word (.docx), Markdown (.md), or text (.txt) files.');
      }

      const maxSize = file.type === 'application/pdf' || file.type.includes('word') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        throw new Error(`File size must be less than ${maxSizeMB}MB for this file type`);
      }

      const fileTypeInfo = getFileTypeInfo(file);
      setUploadStatus(`Reading ${fileTypeInfo.description.toLowerCase()}...`);

      const processedData = await processDocument(file);

      console.log('Document processed successfully:', {
        contentLength: processedData.content.length,
        wordCount: processedData.wordCount
      });

      setUploadStatus('Analyzing document structure...');

      const document = {
        id: Date.now() + Math.random(),
        name: file.name,
        content: processedData.content,
        wordCount: processedData.wordCount,
        size: processedData.size,
        type: processedData.type,
        fileTypeInfo: processedData.fileTypeInfo,
        metadata: processedData.metadata,
        uploadDate: new Date().toISOString(),
        processed: true,
        contentPreview: processedData.content.substring(0, 300).replace(/\s+/g, ' ')
      };

      setUploadStatus('Saving to knowledge base...');

      await addKnowledgeBaseDocument(document);

      setUploadStatus(`Success! "${document.name}" uploaded successfully!

Document Stats:
- File Type: ${document.fileTypeInfo.description}
- ${document.wordCount.toLocaleString()} words processed
- ${formatFileSize(document.size)} file size

The AI can now answer questions about this document.`);

      setTimeout(() => setUploadStatus(''), 10000);
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = error.message;

      if (error.message.includes('CORS') || error.message.includes('browser security')) {
        errorMessage = `Upload failed: Browser security restrictions prevented PDF processing.

Alternative solutions:
1. Try uploading the same content as a Word document (.docx)
2. Copy and paste the text content directly into a text file (.txt)
3. Try using a different browser`;
      }

      setUploadStatus(errorMessage);
      setTimeout(() => setUploadStatus(''), 12000);
    } finally {
      setIsProcessing(false);
      event.target.value = '';
    }
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
  };

  const handleClearMessages = async () => {
    if (confirm('Are you sure you want to clear all chat messages? This action cannot be undone.')) {
      try {
        await clearMessages();
        setSaveStatus('Chat messages cleared successfully!');
        setTimeout(() => setSaveStatus(''), 3000);
      } catch (error) {
        setSaveStatus('Failed to clear messages. Please try again.');
        setTimeout(() => setSaveStatus(''), 5000);
      }
    }
  };

  const getTotalKnowledgeBaseSize = () => {
    return knowledgeBase.reduce((total, doc) => total + (doc.content?.length || 0), 0);
  };

  const getTotalWordCount = () => {
    return knowledgeBase.reduce((total, doc) => total + (doc.wordCount || 0), 0);
  };

  const getProcessedDocumentsCount = () => {
    return knowledgeBase.filter(doc => doc.processed && doc.content && doc.content.length > 0).length;
  };

  const getDocumentIcon = (doc) => {
    if (doc.fileTypeInfo?.icon) {
      return doc.fileTypeInfo.icon === 'FiFileText' ? FiFileText : FiFile;
    }
    return FiFile;
  };

  const getDocumentColor = (doc) => {
    return doc.fileTypeInfo?.color || 'text-gray-500';
  };

  if (chatLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-center py-12">
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-primary-600 mr-3" />
          <span className="text-gray-600">Loading chat settings...</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Chat Configuration Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiSettings} className="w-5 h-5 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-xl font-bold text-gray-900">Chat Configuration</h3>
            <p className="text-sm text-gray-500">Powered by Claude AI</p>
          </div>
        </div>

        {/* Status Messages */}
        {saveStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-3 rounded-lg text-sm flex items-start ${
              saveStatus.includes('successfully') || saveStatus.includes('Success')
                ? 'bg-green-50 border border-green-200 text-green-700'
                : saveStatus.includes('Failed') || saveStatus.includes('Error')
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-blue-50 border border-blue-200 text-blue-700'
            }`}
          >
            <SafeIcon
              icon={
                saveStatus.includes('successfully') || saveStatus.includes('Success')
                  ? FiCheck
                  : saveStatus.includes('Failed') || saveStatus.includes('Error')
                  ? FiAlertTriangle
                  : FiInfo
              }
              className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"
            />
            <div className="whitespace-pre-wrap">{saveStatus}</div>
          </motion.div>
        )}

        {/* Chat Settings */}
        <div className="space-y-4">
          {/* Message History Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <SafeIcon icon={FiSliders} className="w-4 h-4 inline mr-2" />
              Message History Limit
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max={config?.maxMessageHistoryLimit || 100}
                value={messageHistoryLimit}
                onChange={(e) => updateMessageHistoryLimit(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-900 w-12 text-right">
                {messageHistoryLimit}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Number of previous messages to include for context (1-{config?.maxMessageHistoryLimit || 100})
            </p>
          </div>

          {/* Clear Chat History */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={handleClearMessages}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
              Clear Chat History
            </button>
          </div>
        </div>

        {/* AI Status */}
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full mr-3 bg-green-500" />
            <span className="text-sm font-medium text-green-800">
              Claude AI Ready
            </span>
          </div>
          <p className="text-xs text-green-700 mt-1">
            Model: {config?.model || 'claude-sonnet-4-20250514'} | Streaming enabled | Tool use enabled
          </p>
        </div>
      </motion.div>

      {/* Knowledge Base Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-xl font-bold text-gray-900">Knowledge Base</h3>
              <p className="text-sm text-gray-500">Upload documents for AI context</p>
            </div>
          </div>
          <div className="relative">
            <input
              type="file"
              id="file-upload"
              accept={getSupportedExtensions()}
              onChange={handleFileUpload}
              disabled={isProcessing}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <label
              htmlFor="file-upload"
              className={`flex items-center px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                isProcessing
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
              {isProcessing ? 'Processing...' : 'Upload Document'}
            </label>
          </div>
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-4 p-4 border rounded-lg text-sm ${
              uploadStatus.includes('Success') || uploadStatus.includes('successfully')
                ? 'bg-green-50 border-green-200 text-green-700'
                : uploadStatus.includes('failed') || uploadStatus.includes('Error')
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-blue-50 border-blue-200 text-blue-700'
            }`}
          >
            <div className="flex items-start">
              <SafeIcon
                icon={
                  uploadStatus.includes('Success') || uploadStatus.includes('successfully')
                    ? FiCheck
                    : uploadStatus.includes('failed') || uploadStatus.includes('Error')
                    ? FiAlertTriangle
                    : FiInfo
                }
                className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0"
              />
              <div className="whitespace-pre-wrap">{uploadStatus}</div>
            </div>
          </motion.div>
        )}

        {/* Knowledge Base Stats */}
        {knowledgeBase.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-blue-800">
              <div className="text-center">
                <SafeIcon icon={FiFile} className="w-5 h-5 mx-auto mb-1" />
                <div className="font-semibold">{knowledgeBase.length}</div>
                <div className="text-xs">Documents</div>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiCheck} className="w-5 h-5 mx-auto mb-1" />
                <div className="font-semibold">{getProcessedDocumentsCount()}</div>
                <div className="text-xs">Processed</div>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiInfo} className="w-5 h-5 mx-auto mb-1" />
                <div className="font-semibold">{getTotalWordCount().toLocaleString()}</div>
                <div className="text-xs">Total Words</div>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5 mx-auto mb-1" />
                <div className="font-semibold">{(getTotalKnowledgeBaseSize() / 1024).toFixed(1)}KB</div>
                <div className="text-xs">Content Size</div>
              </div>
            </div>
          </div>
        )}

        {/* File Guidelines */}
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Supported File Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <ul className="space-y-1">
              <li>- PDF Documents (.pdf) - Up to 10MB</li>
              <li>- Word Documents (.docx) - Up to 10MB</li>
              <li>- Text Files (.txt) - Up to 5MB</li>
              <li>- Markdown Files (.md) - Up to 5MB</li>
            </ul>
            <ul className="space-y-1">
              <li>- Automatic text extraction</li>
              <li>- Full AI search integration</li>
              <li>- Source attribution in responses</li>
            </ul>
          </div>
        </div>

        {/* Knowledge Base Documents */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 mb-3">
            Uploaded Documents ({knowledgeBase.length})
          </h4>
          {knowledgeBase.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <SafeIcon icon={FiFile} className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No documents uploaded yet</p>
              <p className="text-sm">Upload documents to enhance AI responses</p>
            </div>
          ) : (
            <div className="space-y-3">
              {knowledgeBase.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center flex-1 min-w-0">
                    <SafeIcon
                      icon={getDocumentIcon(doc)}
                      className={`w-5 h-5 mr-3 flex-shrink-0 ${getDocumentColor(doc)}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-medium text-gray-900 truncate">{doc.name}</h5>
                        {doc.fileTypeInfo && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full flex-shrink-0">
                            {doc.fileTypeInfo.description}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatFileSize(doc.size)}</span>
                        <span>{doc.wordCount?.toLocaleString() || 0} words</span>
                        <span>Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}</span>
                      </div>
                      {doc.content && doc.processed && (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-600" />
                            <span className="text-xs text-green-600">
                              Processed & Searchable ({doc.content.length.toLocaleString()} chars)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <button
                      onClick={() => handleViewDocument(doc)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="View document content"
                    >
                      <SafeIcon icon={FiSearch} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveDocument(doc.id, doc.name)}
                      disabled={deletingDocuments.has(doc.id)}
                      className={`p-2 rounded-lg transition-colors min-w-[40px] flex items-center justify-center ${
                        deletingDocuments.has(doc.id)
                          ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                          : confirmDeleteDocument === doc.id
                          ? 'text-white bg-red-600 hover:bg-red-700'
                          : 'text-red-600 hover:bg-red-100'
                      }`}
                      title={
                        deletingDocuments.has(doc.id)
                          ? 'Removing...'
                          : confirmDeleteDocument === doc.id
                          ? 'Click again to confirm deletion'
                          : 'Remove document'
                      }
                    >
                      {deletingDocuments.has(doc.id) ? (
                        <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                      ) : confirmDeleteDocument === doc.id ? (
                        <span className="text-xs font-bold px-1">Confirm</span>
                      ) : (
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Document Viewer Modal */}
      {selectedDocument && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedDocument(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <SafeIcon
                  icon={getDocumentIcon(selectedDocument)}
                  className={`w-6 h-6 mr-3 ${getDocumentColor(selectedDocument)}`}
                />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedDocument.name}</h3>
                  {selectedDocument.fileTypeInfo && (
                    <p className="text-sm text-gray-600">{selectedDocument.fileTypeInfo.description}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedDocument(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>Size: {formatFileSize(selectedDocument.size)}</div>
                <div>Words: {selectedDocument.wordCount?.toLocaleString() || 0}</div>
                <div>Characters: {selectedDocument.content?.length?.toLocaleString() || 0}</div>
                <div>Uploaded: {new Date(selectedDocument.uploadDate).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded-lg border max-h-96 overflow-y-auto">
                {selectedDocument.content || 'No content available'}
              </pre>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Usage Information */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <h3 className="text-lg font-bold text-gray-900 mb-4">How Knowledge Base Works</h3>
        <div className="space-y-4 text-sm text-gray-600">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-primary-600">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Upload Documents</p>
              <p>Upload PDF, Word, text, or markdown files. The AI extracts and indexes all text content.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-primary-600">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Ask Questions</p>
              <p>The AI searches through your documents and business data to provide accurate answers with citations.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-semibold text-primary-600">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Real-Time Data Access</p>
              <p>Claude can query your live business data (revenue, team performance, projects) alongside uploaded documents.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-medium text-green-900 mb-2">Example Questions:</h4>
          <ul className="text-sm text-green-800 space-y-1">
            <li>- "What's our current revenue progress?"</li>
            <li>- "How is John performing on his GM goal?"</li>
            <li>- "What does the uploaded document say about [topic]?"</li>
            <li>- "Show me the sales team performance summary"</li>
            <li>- "What projects are in progress?"</li>
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ChatSettings;
